/**
 * CMS per-market section show/hide as build-time data (DIT-264 / DIT-335 GG2).
 *
 * A page section (e.g. `home.reframe`) can be hidden for a specific market from
 * the CMS admin (PageSection.hidden_in_markets). The API exposes the hidden set
 * per country on `/api/v4/cms/content/`, `fetch-cms-content.mjs` bakes it into
 * `_generated/cms-sections.ts`, and the render layer consults `sectionVisible`
 * to skip a hidden section. Like feature flags, this is baked — one deploy cycle
 * of latency.
 *
 * SEMANTICS — subtractive. A section is visible UNLESS it is listed as hidden for
 * the country. Absent data (offline/CI build with no CMS, or an un-seeded market)
 * keeps every section, so a transient CMS hiccup never blanks a page.
 *
 * Pure data (NFR10): safe for both the Vite render bundle and the Node prerender
 * matrix — no React / browser globals.
 */
import { CMS_HIDDEN_SECTIONS } from './_generated/cms-sections';
import type { Market } from '../i18n/locales';

/** Market → CountrySite code (mirror of content/flags.ts). */
const countryForMarket = (market: Market): string => (market === 'belgium' ? 'be' : 'int');

/** True unless `<page>.<section>` is hidden for `country`. */
export function sectionVisible(country: string, page: string, section: string): boolean {
  return !(CMS_HIDDEN_SECTIONS[country] ?? []).includes(`${page}.${section}`);
}

/** `sectionVisible` keyed by the runtime market (what `useLocale()` exposes). */
export function sectionVisibleForMarket(market: Market, page: string, section: string): boolean {
  return sectionVisible(countryForMarket(market), page, section);
}
